import { useCallback, useEffect, useRef, useState } from "react";
import { Bell, AtSign } from "lucide-react";
import { api } from "../../lib/api";

const timeAgo = (iso) => {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

export function NotificationsBell({ onOpenQuote }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  const load = useCallback(() => api.get("/admin/notifications")
    .then(r => { setItems(r.data.items || []); setUnread(r.data.unread || 0); })
    .catch(() => {}), []);

  useEffect(() => { load(); const iv = setInterval(load, 60000); return () => clearInterval(iv); }, [load]);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const openItem = async (n) => {
    if (!n.read) {
      api.post(`/admin/notifications/${encodeURIComponent(n.id)}/read`).catch(() => {});
      setItems(list => list.map(x => x.id === n.id ? { ...x, read: true } : x));
      setUnread(u => Math.max(0, u - 1));
    }
    setOpen(false);
    if (n.quote_id && onOpenQuote) onOpenQuote(n.quote_id);
  };

  const markAll = async () => {
    await api.post("/admin/notifications/read-all").catch(() => {});
    setItems(list => list.map(x => ({ ...x, read: true })));
    setUnread(0);
  };

  return (
    <div className="relative" ref={boxRef}>
      <button data-testid="notifications-bell" onClick={() => { setOpen(o => !o); if (!open) load(); }}
        className="relative p-2 border border-[#E5E7EB] hover:border-[#C79A3B] text-[#12263A]" title="Notifications">
        <Bell size={16} />
        {unread > 0 && (
          <span data-testid="notifications-unread-badge"
            className="absolute -top-1.5 -right-1.5 bg-[#C0392B] text-white text-[10px] font-bold min-w-[17px] h-[17px] px-1 rounded-full flex items-center justify-center leading-none">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notifications-dropdown" className="absolute right-0 top-full mt-1 w-[340px] max-h-[420px] overflow-y-auto bg-white border border-[#E5E7EB] shadow-lg z-50">
          <div className="flex items-center justify-between px-3 py-2 border-b border-[#E5E7EB]">
            <div className="label-eyebrow">Notifications</div>
            {unread > 0 && (
              <button data-testid="notifications-mark-all" onClick={markAll} className="text-[11px] font-bold text-[#C79A3B] hover:text-[#A87E28]">Mark all read</button>
            )}
          </div>
          {items.length === 0 && (
            <div className="text-sm text-[#6B7280] italic text-center py-8">Nothing yet — you&apos;ll see it here when a teammate @-mentions you.</div>
          )}
          {items.map(n => (
            <button key={n.id} data-testid={`notification-item-${n.id}`} data-quote-id={n.quote_id} onClick={() => openItem(n)}
              className={`block w-full text-left px-3 py-2.5 border-b border-[#F0EAE0] hover:bg-[#FBF3EC] ${n.read ? "opacity-60" : ""}`}>
              <div className="flex items-start gap-2">
                <AtSign size={13} className="mt-0.5 shrink-0 text-[#C79A3B]" />
                <div className="min-w-0">
                  <div className="text-xs text-[#12263A]">
                    <b>{n.actor_name || n.actor}</b> mentioned you{n.client_name ? <> on <b>{n.client_name}</b></> : ""}
                  </div>
                  <div className="text-[11px] text-[#6B7280] truncate mt-0.5">{n.preview}</div>
                  <div className="text-[10px] text-[#9CA3AF] mt-0.5">{timeAgo(n.created_at)}</div>
                </div>
                {!n.read && <span className="ml-auto mt-1 w-2 h-2 rounded-full bg-[#C0392B] shrink-0" />}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
